'use client';

import { ResponsiveOverlay } from '@components/mobile/overlays/responsive-overlay';
import { Button } from '@components/ui/button';
import { useTranslations } from 'next-intl';
import { ReactNode } from 'react';

type ConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel: ReactNode;
  onConfirm: () => void;
  /** Defaults to `common.cancel`. */
  cancelLabel?: ReactNode;
  /** Runs on Cancel; defaults to closing through `onOpenChange`. */
  onCancel?: () => void;
  /** Destructive (red text) unless the action is a safe primary one. */
  confirmVariant?: 'destructive' | 'primary';
  /** Disables the confirm button, e.g. while the action is in flight. */
  confirmDisabled?: boolean;
  /** Optional body between the description and the actions. */
  children?: ReactNode;
};

/**
 * Cancel-or-confirm question on the shared overlay: bottom sheet on phones,
 * centered dialog on desktop.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  onConfirm,
  cancelLabel,
  onCancel,
  confirmVariant = 'destructive',
  confirmDisabled,
  children = null
}: ConfirmDialogProps) {
  const t = useTranslations('common');
  return (
    <ResponsiveOverlay
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel ?? (() => onOpenChange(false))}
          >
            {cancelLabel ?? t('cancel')}
          </Button>
          <Button
            type="button"
            variant={confirmVariant}
            disabled={confirmDisabled}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
    </ResponsiveOverlay>
  );
}
